import { useCallback, useEffect, useRef, useState } from 'react';
import { withPendingHandle } from '@/utils';

/**
 * 通用 pending polling 状态机。
 *
 * fetcher 应返回 Promise<ResponseMO>。首次请求 in-flight 时为 'loading' 态；
 * 命中 staff_pending → 进入 'pending' 态并每 interval ms 重试；
 * 达 timeout ms 仍 pending → 'timeout' 态；fetcher 抛错 → 'error' 态。
 *
 * 注意：'loading' 与 'pending' 语义不同——前者是普通首请求等待，后者代表后端
 * 确实回了 staff_pending（race window）。消费方需据此区分 UI：'loading' 不应
 * 显示"账户准备中"等会误导用户的提示。
 *
 * @param {() => Promise<any>} fetcher 发起请求的函数
 * @param {object} options
 * @param {number} [options.interval=2000] polling 间隔（毫秒）
 * @param {number} [options.timeout=30000] 总超时上限（毫秒）
 * @param {boolean} [options.enabled=true] 是否启用（用于条件触发）
 * @returns {{ status: 'idle'|'loading'|'pending'|'ready'|'timeout'|'error', data: any, error: any, retry: () => void }}
 */
const usePendingPoll = (fetcher, options = {}) => {
  const { interval = 2000, timeout = 30000, enabled = true } = options;

  const [status, setStatus] = useState('idle');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const timerRef = useRef(null);
  const deadlineRef = useRef(0);
  const fetcherRef = useRef(fetcher);
  // 让 fetcher 引用保持最新，避免闭包陷阱
  fetcherRef.current = fetcher;

  // 卸载守卫：tick 是 async，await 期间组件可能已卸载，此后的 setState 会触发
  // "state update on unmounted component" 警告并造成内存泄漏。
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const tick = useCallback(async () => {
    const { pending, res, error: err } = await withPendingHandle(fetcherRef.current);
    if (!mountedRef.current) return;

    if (err) {
      clearTimer();
      setError(err);
      setStatus('error');
      return;
    }

    if (pending) {
      if (Date.now() >= deadlineRef.current) {
        clearTimer();
        setStatus('timeout');
        return;
      }
      // 仅在确实命中 staff_pending 时才置 'pending'，避免首次请求 in-flight
      // 期间 UI 误显示"账户准备中"
      setStatus('pending');
      timerRef.current = setTimeout(tick, interval);
      return;
    }

    // 非 pending：无论 code===0 还是其他 code===1（非 staff_pending）业务错误，都视为终态
    clearTimer();
    setData(res && res.data);
    setStatus('ready');
  }, [clearTimer, interval]);

  const start = useCallback(() => {
    clearTimer();
    deadlineRef.current = Date.now() + timeout;
    setStatus('loading');
    setError(null);
    tick();
  }, [clearTimer, timeout, tick]);

  const retry = useCallback(() => {
    if (!enabled) return;
    start();
  }, [enabled, start]);

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return undefined;
    }
    start();
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { status, data, error, retry };
};

export default usePendingPoll;
